import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { debounce } from "../../utils/debounce";
import { SortedTableDataRowType, TableDataColumnType, TableDataRowType } from "./interface";

/**
 * @slot (default) - The table column elements defining the table structure
 */
@Component({
    tag: "ux-table-data",
    styleUrl: "table-data.scss",
    shadow: false
})
export class TableData {
    @Element() domNode!: HTMLUxTableDataElement;

    /**
     * Set to `true` if the table should cover the full width of its containing element
     */
    @Prop() readonly fullWidth: boolean = false;

    /**
     * The table data
     */
    @Prop() readonly data: TableDataRowType[];

    /**
     * If set to `true` the table will handle sorting internally
     */
    @Prop() readonly selfSorting: boolean = false;

    /** Storage for the table columns */
    @State() tableColumns: TableDataColumnType[] = [];
    /** Storage for the column render methods */
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    @State() tableColumnsMethods: any[] = [];
    /** Storage of the currently selected rows */
    @State() selectedRows: string[] = [];
    /** The sorted data used for rendering the rows */
    @State() sortedData: SortedTableDataRowType[] = [];

    /** Observer watches for column configuration changes  */
    private contentObserver: MutationObserver = new MutationObserver(
        debounce(() => {
            this.updateTableColumns();
        }, 100)
    );

    componentWillLoad() {
        this.updateTableColumns();
        this.setSelectedRows();
    }

    componentDidLoad() {
        this.contentObserver.observe(this.domNode, {
            childList: true,
            subtree: true,
            attributes: true
        });
    }

    disconnectedCallback() {
        this.contentObserver.disconnect();
    }

    private async updateTableColumns() {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        const children = Array.from(this.domNode.children as any);
        this.tableColumns = children.filter(
            // @ts-expect-error Check for typing
            (child: HTMLElement) =>
                /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                typeof (child as any).getColumnMethods === "function"
        ) as TableDataColumnType[];
        this.tableColumnsMethods = await Promise.all(
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
            this.tableColumns.map((col) => (col as any).getColumnMethods())
        );
    }

    private getDataWithInternalIds(): SortedTableDataRowType[] {
        return this.data.map((row, i) => ({
            ...row,
            _uxTableInternalId: `${i}`
        }));
    }

    /**
     * Emitted when a row is selected
     */
    @Event({
        eventName: "uxRowsSelected",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    rowsSelected!: EventEmitter;

    /**
     * Emitted when a row is clicked
     */
    @Event({
        eventName: "uxRowClick",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    rowClick!: EventEmitter;

    @Watch("data")
    setSelectedRows() {
        if (this.data === undefined) {
            return;
        }
        this.sortedData = this.getDataWithInternalIds();
        this.selectedRows = this.sortedData
            .filter((row) => {
                return row.selected;
            })
            .map((row) => row._uxTableInternalId);
    }

    @Watch("selectedRows")
    emitSelectedRows() {
        this.rowsSelected.emit({
            selectedRows: this.sortedData.filter((row: SortedTableDataRowType) =>
                this.selectedRows.includes(row._uxTableInternalId)
            )
        });
    }

    /** Handle table sorting */
    @Listen("uxTableSort")
    sortRows(e: CustomEvent) {
        if (!this.selfSorting) {
            return;
        }
        const sorter = e.target as HTMLUxTableDataSortElement;
        const th = sorter.closest("th");
        const { sortedDirection } = sorter;
        // @ts-expect-error Check for typing
        const titleRow = th.parentElement;
        // @ts-expect-error Check for typing
        const colIndex = [].indexOf.call(titleRow.children, th);
        const getSortString = this.tableColumnsMethods[colIndex].getSortString || (() => "");

        if (sortedDirection === "") {
            return (this.sortedData = this.getDataWithInternalIds());
        }

        const comparer = (a: TableDataRowType, b: TableDataRowType) => {
            const strA = getSortString(a);
            const strB = getSortString(b);
            return sortedDirection === "asc" ? strA.localeCompare(strB) : strB.localeCompare(strA);
        };

        this.sortedData.sort(comparer);
    }

    render() {
        const { fullWidth, selectedRows, sortedData } = this;
        const baseClass = "ux-table-data";
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--full-width`]: fullWidth
                }}
            >
                <slot />
                <div class={`${baseClass}__inner`}>
                    <table class={`${baseClass}__table`}>
                        <thead class={`${baseClass}__thead`}>
                            <tr class={`${baseClass}__tr-head`}>
                                {this.tableColumnsMethods?.map((renderer) => renderer.renderHeader(this))}
                            </tr>
                        </thead>
                        <tbody class={`${baseClass}__tbody`}>
                            {sortedData?.map((tableRow: SortedTableDataRowType) => (
                                <tr
                                    class={{
                                        ["ux-table-data__tr"]: true,
                                        ["ux-table-data__tr--selected"]: selectedRows.includes(tableRow._uxTableInternalId)
                                    }}
                                    onClick={(e) => {
                                        this.rowClick.emit({
                                            row: tableRow,
                                            clickEvent: e
                                        });
                                    }}
                                >
                                    {this.tableColumnsMethods?.map((renderer) => renderer.renderCell(tableRow, this))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Host>
        );
    }
}
